import apiClient from '../utils/apiClient';

/**
 * Shift Preference API Services
 * Quản lý ca ưu tiên của nhân viên
 */

export const shiftPreferenceAPI = {
  /**
   * Lấy danh sách ca ưu tiên với phân trang và lọc
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.employee_id - ID nhân viên (optional)
   * @param {string} params.branch_id - ID chi nhánh (optional)
   * @param {string} params.status - Trạng thái (PENDING | APPROVED | REJECTED) (optional)
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách ca ưu tiên và metadata phân trang
   */
  getShiftPreferences: async (params = {}) => {
    const { employee_id = '', branch_id = '', status = '', page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: '/shift-preferences',
      params: { employee_id, branch_id, status, page, limit },
    });
  },

  /**
   * Lấy thông tin chi tiết ca ưu tiên theo ID
   * @param {string} preferenceId - ID ca ưu tiên
   * @returns {Promise<Object>} Thông tin chi tiết ca ưu tiên
   */
  getShiftPreferenceById: async (preferenceId) => {
    return await apiClient.get({
      url: `/shift-preferences/${preferenceId}`,
    });
  },

  /**
   * Tạo ca ưu tiên mới
   * @param {Object} preferenceData - Dữ liệu ca ưu tiên
   * @param {string} preferenceData.branch_id - ID chi nhánh
   * @param {Array<number>} preferenceData.days_of_week - Mảng các ngày trong tuần [0-6], 0=Chủ nhật, 1=Thứ 2, ..., 6=Thứ 7
   * @param {number} preferenceData.start_hour - Giờ bắt đầu (0-23)
   * @param {number} preferenceData.start_minute - Phút bắt đầu (0-59)
   * @param {number} preferenceData.end_hour - Giờ kết thúc (0-23)
   * @param {number} preferenceData.end_minute - Phút kết thúc (0-59)
   * @param {boolean} preferenceData.is_active - Trạng thái hoạt động (default: true)
   * @returns {Promise<Object>} Thông tin ca ưu tiên vừa tạo
   */
  createShiftPreference: async (preferenceData) => {
    return await apiClient.post({
      url: '/shift-preferences',
      data: preferenceData,
    });
  },

  /**
   * Cập nhật ca ưu tiên
   * @param {string} preferenceId - ID ca ưu tiên
   * @param {Object} updateData - Dữ liệu cập nhật (tất cả fields đều optional)
   * @param {string} updateData.branch_id - ID chi nhánh (optional)
   * @param {Array<number>} updateData.days_of_week - Mảng các ngày trong tuần (optional)
   * @param {number} updateData.start_hour - Giờ bắt đầu (optional)
   * @param {number} updateData.start_minute - Phút bắt đầu (optional)
   * @param {number} updateData.end_hour - Giờ kết thúc (optional)
   * @param {number} updateData.end_minute - Phút kết thúc (optional)
   * @param {boolean} updateData.is_active - Trạng thái hoạt động (optional)
   * @returns {Promise<Object>} Thông tin ca ưu tiên sau khi cập nhật
   */
  updateShiftPreference: async (preferenceId, updateData) => {
    return await apiClient.patch({
      url: `/shift-preferences/${preferenceId}`,
      data: updateData,
    });
  },

  /**
   * Xóa ca ưu tiên
   * @param {string} preferenceId - ID ca ưu tiên
   * @returns {Promise<Object>} Kết quả xóa
   */
  deleteShiftPreference: async (preferenceId) => {
    return await apiClient.delete({
      url: `/shift-preferences/${preferenceId}`,
    });
  },

  /**
   * Phân tích mức độ cạnh tranh và đề xuất ca thay thế
   * @param {string} employeeId - ID nhân viên
   * @param {string} branchId - ID chi nhánh
   * @returns {Promise<Object>} Kết quả phân tích cạnh tranh
   */
  getCompetitionAnalysis: async (employeeId, branchId) => {
    return await apiClient.get({
      url: `/shift-preferences/competition-analysis/${employeeId}`,
      params: { branch_id: branchId },
    });
  },

  /**
   * Admin/Manager chỉnh trực tiếp ca ưu tiên của NV (#190405: áp dụng ngay, bắt buộc lý do)
   * @param {Object} payload - Dữ liệu duyệt batch
   * @param {string} payload.branch_id - ID chi nhánh
   * @param {Array<{id: string, days_of_week: number[]}>} payload.items - Danh sách ca ưu tiên cần chỉnh
   * @param {string} payload.reason - Lý do chỉnh (#190405: bắt buộc, server trả 400 nếu thiếu)
   * @returns {Promise<Object>} Kết quả áp dụng
   */
  adminReviewBatch: async (payload) => {
    return await apiClient.post({
      url: '/shift-preferences/admin/review-batch',
      data: payload,
    });
  },

  /**
   * Lấy số lượng đăng ký mỗi template với thông tin availability
   * @param {string} branchId - ID chi nhánh
   * @returns {Promise<Object>} Danh sách templates với số lượng đăng ký và màu sắc availability
   */
  getTemplateRegistrationCounts: async (branchId) => {
    return await apiClient.get({
      url: '/shift-preferences/template-registration-counts',
      params: { branch_id: branchId },
    });
  },

  /**
   * #230650 (#191801) — Lưới tuần ca ưu tiên (chỉ đọc): ca × thứ, số NV đang làm đăng ký ưu tiên
   * từng ô + ứng viên chờ duyệt hợp ô đó.
   */
  getWeeklyGrid: async (branchId) => {
    return await apiClient.get({
      url: '/shift-preferences/admin/weekly-grid',
      params: { branch_id: branchId },
    });
  },

  /**
   * Admin: LỊCH SỬ thay đổi ca ưu tiên (thêm/sửa/xoá) — #190405: không còn hàng chờ duyệt,
   * nên mặc định lấy toàn bộ lịch sử thay vì chỉ các bản ghi PENDING.
   * @param {Object} params
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} [params.status] - '' (toàn bộ) | PENDING (dữ liệu cũ) | APPROVED | REJECTED
   */
  getChangeRequests: async ({ branch_id, status = '' }) => {
    return await apiClient.get({
      url: '/shift-preferences/admin/change-requests',
      params: { branch_id, status },
    });
  },

  /**
   * Admin: xử lý nốt DỮ LIỆU CŨ — yêu cầu còn PENDING tạo trước #190405.
   * Thao tác ca ưu tiên mới KHÔNG đi qua đây nữa.
   * @param {string} crId - ID change request
   * @param {Object} payload
   * @param {string} payload.action - APPROVE | REJECT
   * @param {string} [payload.note] - Ghi chú admin
   */
  reviewChangeRequest: async (crId, { action, note }) => {
    return await apiClient.post({
      url: `/shift-preferences/admin/change-requests/${crId}/review`,
      data: { action, note },
    });
  },

  /**
   * Admin: Theo dõi trạng thái ca cuối tuần của nhân viên
   * @param {string} branch_id - ID chi nhánh (bắt buộc)
   * @param {boolean} [only_unmet=false] - Chỉ lấy NV chưa đạt điều kiện
   */
  getWeekendTracking: async ({ branch_id, only_unmet = false }) => {
    return await apiClient.get({
      url: '/shift-preferences/admin/weekend-tracking',
      params: { branch_id, only_unmet },
    });
  },

  /**
   * Admin: Lịch sử đăng ký ca cuối tuần của 1 NV (N tuần gần nhất, tính live từ Shift)
   * @param {string} employee_id - ID nhân viên
   * @param {string} branch_id - ID chi nhánh (bắt buộc)
   * @param {number} [weeks=8] - Số tuần lấy lịch sử (1..26)
   */
  getWeekendHistory: async ({ employee_id, branch_id, weeks = 8 }) => {
    return await apiClient.get({
      url: `/shift-preferences/admin/weekend-history/${employee_id}`,
      params: { branch_id, weeks },
    });
  },

  /**
   * Xét trừ TLT đăng ký ca (ADMIN bấm tay, không còn cron tự trừ).
   * Xem trước từng NV đạt/không đạt của 1 tuần, không ghi gì.
   * @param {string} branch_id
   * @param {string} [week_start] - Thứ 2 yyyy-MM-dd, bỏ trống = tuần sau
   */
  getTltPenaltyPreview: async ({ branch_id, week_start }) => {
    return await apiClient.get({
      url: '/shift-preferences/admin/tlt-penalty/preview',
      params: { branch_id, ...(week_start ? { week_start } : {}) },
    });
  },

  /** Chốt tuần: trừ đúng những NV không đạt được tích chọn. */
  applyTltPenalty: async ({ branch_id, week_start, employee_ids }) => {
    return await apiClient.post({
      url: '/shift-preferences/admin/tlt-penalty/apply',
      data: { branch_id, ...(week_start ? { week_start } : {}), employee_ids },
    });
  },

  /** Hoàn TLT 1 dòng đã trừ + gửi Telegram "phục hồi tỷ lệ thưởng". */
  refundTltPenalty: async (penaltyId, { ly_do, da_hoan_tay = false } = {}) => {
    return await apiClient.post({
      url: `/shift-preferences/admin/tlt-penalty/${penaltyId}/refund`,
      data: { ...(ly_do ? { ly_do } : {}), da_hoan_tay },
    });
  },
};
